import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SecureDocumentUploadApiService } from '@app/areas/documents/services/secure-document-upload-api.service';
import { AlertService } from '@services/helpers/alert/alert.service';
import { ButtonComponent } from '@shared/components/buttons/button/button.component';
import { CardComponent } from '@shared/components/cards/card/card.component';
import { DocumentsComponent } from '@app/areas/documents/components/documents/documents.component';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { FilePreviewService } from '@shared/services/file-preview/file-preview.service';
import { FilePreviewItem } from '@shared/models/file-preview-item';
import { DocumentDto, ListDirectoryResponse } from '@app/areas/documents/services/patient-documents-api.service';

const STORAGE_KEY = 'fitmed_secure_upload_token';

@Component({
  selector: 'app-secure-document-upload',
  standalone: true,
  imports: [ButtonComponent, CardComponent, DocumentsComponent, ModalComponent],
  templateUrl: './secure-document-upload.component.html',
})
export class SecureDocumentUploadComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(SecureDocumentUploadApiService);
  private readonly alertService = inject(AlertService);
  private readonly previewService = inject(FilePreviewService);

  token = '';
  loadingFiles = false;
  loadingUpload = false;
  confirming = false;
  confirmModalOpen = false;
  locked = false;
  success = '';
  error = '';
  uploadsInFlight = 0;
  subdirs: Array<any> = [];
  docs: DocumentDto[] = [];

  private readonly previewObjectUrls = new Map<string, string>();

  ngOnInit(): void {
    const tokenFromUrl = (this.route.snapshot.queryParamMap.get('token') ?? '').trim();
    const tokenFromSession = (sessionStorage.getItem(STORAGE_KEY) ?? '').trim();
    const token = tokenFromUrl || tokenFromSession;

    if (tokenFromUrl) {
      sessionStorage.setItem(STORAGE_KEY, tokenFromUrl);
    } else if (tokenFromSession) {
      sessionStorage.setItem(STORAGE_KEY, tokenFromSession);
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { token: tokenFromSession },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    this.token = token;

    if (!this.token) {
      this.error = 'Missing secure upload token.';
      return;
    }

    this.fetch();
  }

  ngOnDestroy(): void {
    for (const url of this.previewObjectUrls.values()) {
      URL.revokeObjectURL(url);
    }
    this.previewObjectUrls.clear();
  }

  get uploadsDisabled(): boolean {
    return this.locked || !this.token;
  }

  openConfirmModal(): void {
    if (!this.token || this.locked || this.confirming) {
      return;
    }
    this.confirmModalOpen = true;
  }

  closeConfirmModal(): void {
    if (this.confirming) {
      return;
    }
    this.confirmModalOpen = false;
  }

  onUpload(payload: { file: File; dirPath: string }): void {
    const token = this.token;
    if (!token || this.locked) {
      return;
    }

    this.uploadsInFlight += 1;
    this.loadingUpload = true;
    this.success = '';
    this.error = '';

    this.api.upload(token, payload.file)
      .pipe(finalize(() => {
        this.uploadsInFlight = Math.max(0, this.uploadsInFlight - 1);
        this.loadingUpload = this.uploadsInFlight > 0;
      }))
      .subscribe({
        next: () => {
          this.alertService.success('File uploaded successfully.');
          this.fetch();
        },
        error: (err) => {
          console.error('[SecureDocumentUpload] upload failed', err);
          this.error = 'Unable to upload the file. Please try again.';
        },
      });
  }

  onDownload(documentId: string): void {
    const token = this.token;
    if (!token || this.locked) {
      return;
    }

    const file = this.docs.find((doc) => doc.id === documentId);
    const name = file?.filename || 'download.bin';

    this.api.download(token, documentId).subscribe({
      next: (blob) => this.saveBlob(blob, name),
      error: (err) => {
        console.error('[SecureDocumentUpload] download failed', err);
        this.error = 'Unable to download the file. Please try again.';
      },
    });
  }

  async onPreview(documentId: string): Promise<void> {
    const token = this.token;
    if (!token || this.locked) {
      return;
    }

    const file = this.docs.find((doc) => doc.id === documentId);
    if (!file) return;

    const item: FilePreviewItem = {
      id: file.id,
      name: file.filename,
      mime: file.mimeType,
      source: 'document',
      previewLoader: async () => {
        try {
          const cached = this.previewObjectUrls.get(documentId);
          if (cached) {
            return cached;
          }

          const blob = await firstValueFrom(this.api.download(token, documentId));
          const previewUrl = URL.createObjectURL(blob);
          this.previewObjectUrls.set(documentId, previewUrl);
          return previewUrl;
        } catch (error) {
          console.error('[SecureDocumentUpload] preview failed', error);
          return '';
        }
      },
    };

    this.previewService.open([item]);
  }

  onRenameDocument(payload: { documentId: string; newFilename: string }): void {
    const token = this.token;
    if (!token || this.locked) {
      return;
    }

    const newFilename = (payload.newFilename || '').trim();
    if (!newFilename) return;

    this.api.rename(token, payload.documentId, newFilename).subscribe({
      next: () => this.fetch(),
      error: (err) => {
        console.error('[SecureDocumentUpload] rename failed', err);
        this.error = 'Unable to rename the file. Please try again.';
      },
    });
  }

  onDeleteDocument(documentId: string): void {
    const token = this.token;
    if (!token || this.locked) {
      return;
    }

    if (!window.confirm('Delete this file?')) {
      return;
    }

    this.api.delete(token, documentId).subscribe({
      next: () => {
        this.alertService.success('File deleted successfully.');
        this.fetch();
      },
      error: (err) => {
        console.error('[SecureDocumentUpload] delete failed', err);
        this.error = 'Unable to delete the file. Please try again.';
      },
    });
  }

  confirmUploads(): void {
    const token = this.token;
    if (!token || this.locked || this.confirming) {
      return;
    }

    this.confirming = true;
    this.success = '';
    this.error = '';

    this.api.confirm(token)
      .pipe(finalize(() => (this.confirming = false)))
      .subscribe({
        next: () => {
          sessionStorage.removeItem(STORAGE_KEY);
          this.token = '';
          this.locked = true;
          this.confirmModalOpen = false;
          this.success = 'Uploads have been confirmed. This secure link is now closed.';
        },
        error: (err) => {
          console.error('[SecureDocumentUpload] confirm failed', err);
          this.error = 'Unable to confirm this upload link. Please try again.';
        },
      });
  }

  private fetch(): void {
    const token = this.token;
    if (!token || this.locked) {
      return;
    }

    this.loadingFiles = true;
    this.success = '';
    this.error = '';

    this.api.list(token)
      .pipe(finalize(() => (this.loadingFiles = false)))
      .subscribe({
        next: (res: ListDirectoryResponse) => {
          this.subdirs = res.directories ?? [];
          this.docs = res.documents ?? [];
        },
        error: (err) => {
          console.error('[SecureDocumentUpload] list failed', err);
          const message = `${err?.error?.message ?? err?.message ?? err ?? ''}`.toLowerCase();
          if (message.includes('revoked') || message.includes('expired') || message.includes('invalid')) {
            sessionStorage.removeItem(STORAGE_KEY);
            this.locked = true;
            this.token = '';
            this.error = 'This secure link is no longer available.';
            return;
          }
          this.error = 'Unable to load uploaded files. Please try again.';
        },
      });
  }

  private saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noreferrer';
    anchor.click();
    URL.revokeObjectURL(url);
  }
}
